<template>
  <div class="">
    <div class="border-b py-2 sm:flex sm:items-center">
      <div class="flex flex-auto items-center space-x-2">
        <div class="shrink-0">
          <!--          <mdicon name="email" size="26" class="text-orange-600/70" aria-hidden="true"></mdicon>-->
        </div>
        <div class="">
          <h1 class="text-xl font-semibold text-gray-700">{{ title }}</h1>
        </div>
      </div>
      <Button icon="pi pi-plus" label="Add Macro Trigger" size="small" @click="onNewMacroTag" />
    </div>

    <div class="">
      <DataTable :value="allMacrosTags" paginator :rows="25" :rows-per-page-options="[25, 50, 100]" removable-sort table-style="min-width: 50rem" class="" row-hover>
        <Column field="macrosId" header="Macro" sortable>
          <template #body="slotProps">
            <div class="flex items-center">
              <mdicon name="label-variant" size="22" class="mr-1 text-slate-300" aria-hidden="true"></mdicon>
              <div class="cursor-pointer font-semibold hover:underline" @click="onEditMacroTag(slotProps.data)">{{ macrosObj[slotProps.data.macrosId] }}</div>
            </div>
          </template>
        </Column>
        <Column field="id" header="Tag" sortable> </Column>
        <Column field="integrationName" header="Integration" sortable>
          <template #body="slotProps">
            <div class="flex items-center">
              <span class="cursor-pointer font-semibold hover:underline">{{ integrationsObj[slotProps.data.integrationName] }}</span>
            </div>
          </template>
        </Column>
        <Column field="targetFieldId" header="Target Field" sortable> </Column>
        <Column field="updated" header="Last Updated" sortable>
          <template #body="slotProps"> {{ slotProps.data?.updated ? dayjs(slotProps.data.updated).format('MM/DD/YYYY') : '' }} </template>
        </Column>
        <Column field="updatedBy" header="Updated By" sortable> </Column>
        <Column style="width: 1%">
          <template #body="slotProps">
            <Button type="button" size="small" icon="pi pi-ellipsis-v" rounded severity="secondary" @click="(event) => toggleMacrosTagMenu(event, slotProps?.data)" />
          </template>
        </Column>
      </DataTable>
    </div>
    <Menu id="overlay_menu" ref="emailMenu" :model="emailMenuItems" :popup="true">
      <template #item="{ item, props }">
        <a v-if="!item.hidden" class="flex items-center" v-bind="props.action">
          <mdicon :name="item.icon" size="18" class="mr-0.5 text-slate-400"></mdicon>
          <span class="ml-2">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
    <ConfirmDialog group="delCat">
      <template #message="slotProps">
        <div class="flex flex-col gap-2">
          <div class="text-sm">Please confirm deleting</div>
          <div class="text-lg font-medium">{{ currentMacroTag.id }}</div>
        </div>
      </template>
    </ConfirmDialog>
    <SettingsDialogMacrosTags ref="dialogEditMacros" type="Macros" @save="onSaveMacrosTag" />
  </div>
</template>

<script setup>
  import { useAuthStore } from '@/store/AuthStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useBotsStore } from '@/store/BotsStore';
  import { ref, computed, toRaw, onMounted, watch, watchEffect, reactive } from 'vue';
  import { useToast } from 'vue-toastification';
  import DataTable from 'primevue/datatable';
  import Column from 'primevue/column';
  import SettingsDialogMacrosTags from '@/components/Settings/dialogs/SettingsDialogMacrosTags.vue';
  import { fbDb } from '@/firebaseApp';
  import { collection, getDocs, onSnapshot } from 'firebase/firestore';
  import dayjs from 'dayjs';
  import Button from 'primevue/button';
  import Menu from 'primevue/menu';
  import ConfirmDialog from 'primevue/confirmdialog';
  import { useConfirm } from 'primevue/useconfirm';
  import { useCollection } from 'vuefire';
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  const botsStore = useBotsStore();
  const confirm = useConfirm();

  const props = defineProps({
    integrationsAllowed: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: '',
    },
  });

  const dialogEditMacros = ref(null);
  const emailMenu = ref(null);
  const allMacrosTags = ref([]);
  let integrations = ref([]);
  let macrosData = ref([]);
  let integrationsObj = ref({});
  let macrosObj = ref({});
  let macroTriggerEdited = null;

  // [sv2] TODO consider optimizing this. Re-Reading all from firebase each time component is mounted is inefficient.
  (async () => {
    await settingsStore.getElasticIndexSpan();
    const integrationsAllowed = props.integrationsAllowed.filter((ia) => settingsStore[`${ia}IntegrationEnabled`]);
    const integrationsSnapshot = await getDocs(collection(fbDb, `tenants/${authStore.tenantId}/integrations`));
    const macrosSnapshot = await getDocs(collection(fbDb, `tenants/${authStore.tenantId}/macros`));
    macrosData.value = macrosSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    macrosObj.value = macrosData.value?.reduce((acc, el) => {
      return Object.assign(acc, { [el.id]: el.name });
    }, {});
    integrations.value = integrationsSnapshot.docs
      ?.filter((di) => integrationsAllowed.includes(di.id))
      .map((doc) => ({
        id: doc.id,
        name: doc.id.replace(/\s/g, '').replace(/^./, (match) => match.toUpperCase()),
      }));
    integrationsObj.value = integrations.value?.reduce((acc, el) => {
      return Object.assign(acc, { [el.id]: el.name });
    }, {});
    const integrationEmails = {};
    integrations.value.forEach((integration) => {
      const tagMacrosRef = collection(fbDb, `tenants/${authStore.tenantId}/integrations/${integration.id}/tagMacros`);
      onSnapshot(tagMacrosRef, (snapshot) => {
        integrationEmails[integration.id] = snapshot.docs.map((doc) => ({
          id: doc.id,
          integrationName: integration.id,
          ...doc.data(),
        }));

        allMacrosTags.value = Object.values(integrationEmails).flat();
      });
    });
  })();

  const currentMacroTag = ref(null);
  const emailMenuItems = ref([
    {
      label: 'Delete',
      icon: 'minus',
      command: () => {
        confirm.require({
          group: 'delCat',
          header: `Delete Macro Trigger`,
          acceptIcon: 'pi pi-check',
          rejectIcon: 'pi pi-times',
          rejectClass: 'p-button-outlined p-button-sm',
          acceptClass: 'p-button-sm p-button-danger',
          rejectLabel: 'Cancel',
          acceptLabel: 'Delete',
          accept: () => {
            onDeleteMacrosTag(currentMacroTag.value);
          },
          reject: () => {
            console.log(`Macro Trigger delete canceled`);
          },
        });
      },
    },
  ]);

  async function onEditMacroTag(macrosTagDoc) {
    console.log(`onEditMacrosTag:`, macrosTagDoc);
    macroTriggerEdited = toRaw(macrosTagDoc);
    dialogEditMacros.value.start(macrosTagDoc, toRaw(integrations.value), toRaw(macrosData.value));
  }

  async function onNewMacroTag() {
    macroTriggerEdited = null;
    dialogEditMacros.value.start({}, toRaw(integrations.value), toRaw(macrosData.value));
  }

  async function onSaveMacrosTag({ integrationName, ...payload }) {
    if (macroTriggerEdited && macroTriggerEdited.id !== payload.id) {
      await settingsStore.deleteMacros(integrationName, { id: macroTriggerEdited.id });
    }
    await settingsStore.updateMacros(integrationName, payload);
  }

  async function onDeleteMacrosTag({ botId, id, updated, updatedBy, integrationName }) {
    await settingsStore.deleteMacros(integrationName, { id });
  }

  function toggleMacrosTagMenu(event, macrosTagDoc) {
    currentMacroTag.value = macrosTagDoc;
    emailMenu.value.toggle(event);
  }

  function getBotName(botId) {
    if (!botId) {
      return '-';
    }
    const bot = botsStore.getBot(botId);
    if (!bot) {
      return '-';
    }
    return bot?.name || '-';
  }
</script>
